import React from 'react';
import { Box, Container, Typography, styled, Stack } from '@mui/material';
import { motion } from 'framer-motion';
import TerminalIcon from '@mui/icons-material/Terminal';

const HeroWrapper = styled(Box)({
    padding: '140px 0 80px 0',
    background: '#030712',
    position: 'relative',
    overflow: 'hidden',
    textAlign: 'center',
});

// Background decorative elements
const GradientBlur = styled(Box)({
    position: 'absolute',
    width: '40vw',
    height: '40vw',
    background: 'radial-gradient(circle, rgba(59, 130, 246, 0.08) 0%, transparent 70%)',
    top: '-10%',
    left: '50%',
    transform: 'translateX(-50%)',
    filter: 'blur(80px)',
    zIndex: 0,
});

const StatBox = ({ label, value }) => (
    <Box sx={{ textAlign: 'center' }}>
        <Typography variant="h4" sx={{ color: '#fff', fontWeight: 900, mb: 0.5 }}>
            {value}
        </Typography>
        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, letterSpacing: 1.5 }}>
            {label}
        </Typography>
    </Box>
);

const PortfolioHero = () => {
    return (
        <HeroWrapper>
            <GradientBlur />
            <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                
                {/* Badge */}
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <Stack 
                        direction="row" 
                        spacing={1} 
                        alignItems="center" 
                        justifyContent="center"
                        sx={{ 
                            mb: 4, 
                            background: 'rgba(255,255,255,0.03)', 
                            width: 'fit-content', 
                            mx: 'auto',
                            px: 2, py: 0.8,
                            borderRadius: '50px',
                            border: '1px solid rgba(255,255,255,0.08)'
                        }}
                    >
                        <TerminalIcon sx={{ color: '#2dd4bf', fontSize: '1rem' }} />
                        <Typography variant="caption" sx={{ color: '#fff', fontWeight: 600, letterSpacing: 1 }}>
                            PROJECTS ARCHIVE 2024-25
                        </Typography>
                    </Stack>
                </motion.div>

                {/* Main Heading */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                >
                    <Typography variant="h1" sx={{ 
                        fontWeight: 900, 
                        color: '#fff', 
                        fontSize: { xs: '2.8rem', md: '4.5rem' },
                        lineHeight: 1.1,
                        mb: 3,
                        letterSpacing: '-0.03em'
                    }}>
                        Engineering <span style={{ color: '#3b82f6' }}>Impactful</span> <br />
                        Digital Experiences.
                    </Typography>
                </motion.div>

                {/* Subtext */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                >
                    <Typography variant="body1" sx={{ 
                        color: '#94a3b8', 
                        maxWidth: '700px', 
                        mx: 'auto', 
                        fontSize: '1.1rem', 
                        lineHeight: 1.8,
                        mb: 6
                    }}>
                        A curated showcase of native Android apps, high-performance web systems, 
                        and complex hardware integrations. Each project solve a unique problem 
                        with technical precision.
                    </Typography>
                </motion.div>

                {/* Stats / Proof of Work */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.5 }}
                >
                    <Stack 
                        direction="row" 
                        spacing={{ xs: 4, md: 8 }} 
                        justifyContent="center"
                        sx={{ 
                            borderTop: '1px solid rgba(255,255,255,0.05)', 
                            pt: 6,
                            mt: 2
                        }}
                    >
                        <StatBox value="15+" label="Native Apps" />
                        <StatBox value="3+" label="WEB Systems" />
                        <StatBox value="6+" label="Hybrid Apps" />
                    </Stack>
                </motion.div>
            </Container>
        </HeroWrapper>
    );
};

export default PortfolioHero;